/**
 * The small parts of "Comment on GitLab" that more than one screen needs (issue
 * #99, phase C), kept out of the component files so those export only components.
 *
 * Two places lead to the same section: an element's own page, and the comment
 * button on a row of the change list. They agree on where it is (`COMMENT_HASH`),
 * on how the counts are keyed, and on how a changed element finds its
 * page - which is why this is one file and not a copy in each.
 */

import { useEffect, useState } from 'react'
import { api } from '../../api'
import type { DiscussionList, ElementChange, Schema } from '../../schema'

/** The anchor of the section on an element's page, and what a link to it ends with. */
export const COMMENT_ID = 'comment'
export const COMMENT_HASH = `#${COMMENT_ID}`

/** What the section says when there is no merge request to comment on. */
export const NO_MERGE_REQUEST =
  'A GitLab diff comment belongs to a merge request, and this branch has none open. ' +
  'Propose it on Version Control first.'

/**
 * How many threads name each element, keyed by `thread.element`: the one
 * canonical key the server gives an element (its uid, else its qualified name).
 */
export function countByElement(list: DiscussionList | null): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const thread of list?.threads ?? []) {
    if (thread.element) counts[thread.element] = (counts[thread.element] ?? 0) + 1
  }
  return counts
}

/** The count for one changed element: by its uid, else by its qualified name - the canonical key. */
export function countFor(counts: Record<string, number>, row: ElementChange): number {
  return (row.uid && counts[row.uid]) || counts[row.qualified_name] || 0
}

/**
 * Where a changed element's own page is, or null when it has none.
 *
 * The change list knows an element by its syntax keyword, and a page exists for
 * a kind the registry declares - found by that keyword, the same way the server
 * files it. The page is addressed by qualified name, one of the columns the
 * server looks an element up by. Anything else (a doc comment, an attribute) is
 * part of something else's page and has none of its own.
 */
export function elementPage(schema: Schema, row: ElementChange): string | null {
  if (!row.keyword) return null
  const kind = schema.kinds.find(
    (each) =>
      each.keyword === row.keyword || (each.keywords ?? []).some((choice) => choice.name === row.keyword),
  )
  if (!kind || !row.qualified_name) return null
  return `/${kind.plural}/${encodeURIComponent(row.qualified_name)}`
}

/**
 * Where a row of the change list may LINK to, or null when it should not.
 *
 * The one rule for the name link and for the comment button, so the two cannot
 * disagree about the same row (issue #102). A REMOVED element is not on this
 * branch, so the page would not find it; a kind with no page has nowhere to go.
 */
export function elementLink(schema: Schema, row: ElementChange): string | null {
  return row.status === 'removed' ? null : elementPage(schema, row)
}

/** What the editor needs to open one element: the registry route and the id its page uses. */
export interface EditTarget {
  plural: string
  id: string
  /** For the dialog's title. */
  label: string
}

/** The editor target of a changed element, or null for a removed one or a kind with no page. */
export function editTargetOf(schema: Schema, row: ElementChange): EditTarget | null {
  const page = elementLink(schema, row)
  if (!page) return null
  const [, plural] = page.split('/')
  return { plural, id: row.qualified_name, label: row.qualified_name }
}

/**
 * The editor target a discussion thread's element key names, found in the
 * branch's change list; null when the change list does not know it.
 *
 * A thread keys its element by uid, else qualified name (the canonical key), and
 * carries no kind. The change list does, so it is the lookup. An element the
 * branch did not change has no row and so no Edit here: its own page has the
 * form (the thread's "Comment" link leads there).
 */
export function editTargetForThread(
  schema: Schema,
  changes: { elements: ElementChange[]; requirements: { identifier: string; qualified_name: string; status: string }[] } | null,
  key: string | null | undefined,
): EditTarget | null {
  if (!key || !changes) return null
  const requirement = changes.requirements.find(
    (row) => row.identifier === key || row.qualified_name === key,
  )
  if (requirement) {
    return requirement.status === 'removed'
      ? null
      : { plural: 'requirements', id: requirement.identifier, label: requirement.identifier }
  }
  const element = changes.elements.find((row) => row.uid === key || row.qualified_name === key)
  return element ? editTargetOf(schema, element) : null
}

/** Where a requirement's own page is: the route the requirement list and the RTM link to. */
export function requirementPage(identifier: string): string {
  return `/requirements/${encodeURIComponent(identifier)}`
}

/**
 * The counts of threads per element on the open merge request, from ONE read.
 *
 * Best effort by design: the counts are decoration on a list that is useful
 * without them, so a failed read leaves them empty (`failed`) rather than
 * raising anything, and the list carries on.
 */
export function useElementThreadCounts(slug: string): {
  counts: Record<string, number>
  failed: boolean
} {
  const [state, setState] = useState<{ counts: Record<string, number>; failed: boolean }>({
    counts: {},
    failed: false,
  })

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    api
      .listDiscussions(slug)
      .then((list) => {
        if (!cancelled) setState({ counts: countByElement(list), failed: false })
      })
      .catch(() => {
        if (!cancelled) setState({ counts: {}, failed: true })
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  return state
}
